import React from 'react';
import { CalendarPlus, Loader2, Lock, Unlock, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Text } from '@/components/flup';

/**
 * The one bar every slot action lives on.
 *
 * The screen it replaced put a status radio group, a save button and a "create
 * booking" button *inside each time-of-day card* — four copies of the same
 * controls, each only applying to its own band. Here selection is global and
 * the actions come to the vendor at the bottom of the screen, so the answer to
 * "how do I block this slot" is the same wherever they are on the board.
 */

interface SelectionBarProps {
  count: number;
  totalValue: number;
  /** True when every picked slot is currently blocked — the bar then offers "Open" only. */
  allBlocked: boolean;
  /** True when every picked slot is open — the bar then offers "Block" only. */
  allOpen: boolean;
  saving: boolean;
  onClear: () => void;
  onOpen: () => void;
  onBlock: () => void;
  onBook: () => void;
}

const Action: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  tone?: 'ghost' | 'solid' | 'danger';
  disabled?: boolean;
}> = ({ onClick, icon, label, tone = 'ghost', disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'inline-flex items-center gap-2 h-10 px-4 rounded-flup-sm whitespace-nowrap transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
      'disabled:opacity-45 disabled:pointer-events-none',
      // The one teal button is the action that creates something; the rest
      // only change state on slots that already exist.
      tone === 'solid' && 'bg-flup-brand text-white hover:bg-flup-brand-press',
      tone === 'danger' && 'bg-white/10 text-white hover:bg-white/20',
      tone === 'ghost' && 'bg-white/10 text-white hover:bg-white/20',
    )}
  >
    {icon}
    <Text variant="labelSemi" tone="inherit" as="span">
      {label}
    </Text>
  </button>
);

const SelectionBar: React.FC<SelectionBarProps> = ({
  count,
  totalValue,
  allBlocked,
  allOpen,
  saving,
  onClear,
  onOpen,
  onBlock,
  onBook,
}) => {
  if (count === 0) return null;

  return (
    <div className="sticky bottom-4 z-30 mt-4 animate-fade-in">
      <div className="flex flex-wrap items-center gap-3 p-3 pl-4 rounded-flup-xl bg-flup-nav shadow-flup-float">
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="grid place-items-center w-8 h-8 shrink-0 rounded-flup-xs bg-white/10 text-white hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4" aria-hidden />
        </button>

        <div className="min-w-0 mr-auto">
          <Text variant="labelSemi" tone="onDark" as="p">
            {count} slot{count === 1 ? '' : 's'} selected
          </Text>
          <Text variant="micro" tone="onDarkMuted" as="p" className="nums">
            ₹{Math.round(totalValue).toLocaleString('en-IN')} total
          </Text>
        </div>

        {saving ? (
          <span className="inline-flex items-center gap-2 h-10 px-4 text-white">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
            <Text variant="labelSemi" tone="inherit" as="span">
              Saving…
            </Text>
          </span>
        ) : (
          <div className="flex items-center gap-2">
            {/* Only the move that makes sense is offered: a set of open slots
                can be blocked, a set of blocked slots can be reopened, and a
                mixed set gets both. */}
            {!allOpen ? (
              <Action onClick={onOpen} icon={<Unlock className="w-4 h-4" />} label="Open" />
            ) : null}
            {!allBlocked ? (
              <Action onClick={onBlock} icon={<Lock className="w-4 h-4" />} label="Block" tone="danger" />
            ) : null}
            <Action
              onClick={onBook}
              icon={<CalendarPlus className="w-4 h-4" />}
              label="Book for customer"
              tone="solid"
              disabled={allBlocked}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SelectionBar);
